'use client';

import React, { useEffect, useState } from 'react';
import { PagePayoutSchema } from '@/sanity/pages/PagePayoutTemplate';

import { cn } from '@/lib/utils';
import Image from 'next/image';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/shadcn/ui/carousel';

import laurels from '@/public/payout/laurels.svg';
import ipad from '@/public/global/ipad.png';
import { PageAssetsSchema } from '@/sanity/pages/PageAssetsTemplate';
import { motion } from 'framer-motion';
import bulletIcon from '@/public/assets/bulletIcon.svg';
const ease = [0.25, 0.1, 0.25, 1] as const;

export default function RewardsSection({
  data,
  className,
}: {
  data: PageAssetsSchema;
  className?: string;
}) {
  const bgCards: { bgColor: string }[] = [
    { bgColor: '#85D0A6' },
    { bgColor: '#93BCDB' },
    { bgColor: '#FFF7E2' },
  ];
  return (
    <section className={cn('relative overflow-clip', className)}>
      <div className="container px-[3rem] lg:px-0 max-w-none flex flex-col gap-[1.25rem] items-center pb-[8rem] lg:pt-[8rem] lg:pb-[2rem] relative">
        {data?.rewardsChip && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.2 }}
            className="mb-0 font-blacker italic text-[2rem] text-black leading-[1.5]"
          >
            {data.rewardsChip}
          </motion.p>
        )}

        <motion.div
          className="richtext  lg:richtext-center prose-p:text-[2.25rem] lg:prose-p:text-[2rem] prose-p:w-[min(59.75rem,100%)] [text-wrap-style:pretty] flex flex-col gap-[2rem]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {data?.rewardsText && (
            <PortableText
              value={data?.rewardsText}
              components={richTextComponent}
            />
          )}
        </motion.div>

        {data.rewardsList && (
          <div className="w-[min(86.25rem,100%)] flex flex-col gap-[2.5rem] mt-[4rem] lg:mt-[6rem]">
            {data.rewardsList.map((item, index) => (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.3,
                  ease: [0.25, 0.46, 0.45, 0.94],
                  delay: index * 0.06,
                }}
                key={'rewardItem' + index}
                className="flex flex-col p-[2rem] gap-[2rem] lg:flex-row lg:items-center lg:p-0 bg-[#F4F4F4] rounded-[0.25rem] lg:h-[25rem] overflow-hidden"
              >
                {/* Image */}
                <div className="w-[10.5rem] h-[13.375rem] rounded-[2.5rem] shrink-0 lg:overflow-hidden lg:rounded-none lg:w-[26rem] lg:h-full">
                  <Image
                    src={item.image}
                    alt=""
                    className="!w-full !h-full !object-contain lg:!object-cover"
                  />
                </div>

                {/* Text */}
                <div className="flex  flex-col justify-center gap-[1.5rem] lg:p-[4rem] lg:py-[3rem] lg:pr-[1rem] min-w-0">
                  <div
                    className="richtext richtext-start
                    prose-headings:text-[#103037] prose-headings:text-[3rem] prose-headings:font-medium prose-headings:leading-[1.06] prose-headings:tracking-[-0.15rem] prose-headings:mb-0
                    prose-p:text-[#103037]/80 prose-p:text-[2rem] prose-p:leading-[1.45] prose-p:tracking-[-0.1rem] prose-p:mb-0
                    [&_img]:hidden prose-ul:list-disc prose-li:list-item prose-li:ms-[3rem] prose-li:text-[2rem] prose-li:leading-[1.45] prose-li:tracking-[-0.1rem] prose-li:text-[#103037]/80 [&_li>span>span]:!font-geist-mono flex flex-col gap-[1.5rem]"
                  >
                    {item.text && (
                      <PortableText
                        value={item.text}
                        components={richTextComponent}
                      />
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
